import React, { useState, useMemo } from "react";
import { Card, Table, Col } from "react-bootstrap";
import {
  Table as TableIcon,
  ArrowDownUp,
  Download,
  Search,
} from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { handleClaimDashboardFilter } from "../config/claimDashboardSlice";

const PAYER_COLORS = [
  "#2563EB", // Bupa - Blue
  "#0D9488", // Tawuniya - Teal
  "#7C3AED", // MedGulf - Purple
  "#EA580C", // GIG - Orange
  "#DB2777", // Malath - Pink
  "#16A34A", // Al Rajhi Takaful - Green
  "#CA8A04", // Allianz SF - Yellow/Gold
  "#0EA5E9", // AXA Cooperative - Sky Blue
  "#DC2626", // Walaa - Red
  "#64748B", // Arabian Shield - Slate
];

const DEFAULT_FINANCIAL_SUMMARY_DATA = [
  {
    payer: "Bupa Arabia",
    code: "BUPA",
    claimsCount: 4850,
    grossAmount: 1350000,
    netSubmitted: 1200000,
    approvedAmount: 1050000,
    rejectedAmount: 150000,
    pendingAmount: 65000,
    approvalRate: 87.5,
    rejectionRate: 12.5,
    avgClaimValue: 278.3,
    color: PAYER_COLORS[0],
  },
  {
    payer: "Tawuniya",
    code: "TAW",
    claimsCount: 3920,
    grossAmount: 1080000,
    netSubmitted: 980000,
    approvedAmount: 890000,
    rejectedAmount: 90000,
    pendingAmount: 50000,
    approvalRate: 90.8,
    rejectionRate: 9.2,
    avgClaimValue: 275.5,
    color: PAYER_COLORS[1],
  },
  {
    payer: "MedGulf",
    code: "MEDG",
    claimsCount: 2450,
    grossAmount: 650000,
    netSubmitted: 580000,
    approvedAmount: 510000,
    rejectedAmount: 70000,
    pendingAmount: 35000,
    approvalRate: 87.9,
    rejectionRate: 12.1,
    avgClaimValue: 265.3,
    color: PAYER_COLORS[2],
  },
  {
    payer: "GIG Saudi",
    code: "GIG",
    claimsCount: 1840,
    grossAmount: 490000,
    netSubmitted: 430000,
    approvedAmount: 385000,
    rejectedAmount: 45000,
    pendingAmount: 28000,
    approvalRate: 89.5,
    rejectionRate: 10.5,
    avgClaimValue: 266.3,
    color: PAYER_COLORS[3],
  },
  {
    payer: "Malath Insurance",
    code: "MAL",
    claimsCount: 1280,
    grossAmount: 340000,
    netSubmitted: 300000,
    approvedAmount: 268000,
    rejectedAmount: 32000,
    pendingAmount: 22000,
    approvalRate: 89.3,
    rejectionRate: 10.7,
    avgClaimValue: 265.6,
    color: PAYER_COLORS[4],
  },
  {
    payer: "Al Rajhi Takaful",
    code: "RAJ",
    claimsCount: 1150,
    grossAmount: 310000,
    netSubmitted: 275000,
    approvedAmount: 248000,
    rejectedAmount: 27000,
    pendingAmount: 18000,
    approvalRate: 90.1,
    rejectionRate: 9.9,
    avgClaimValue: 269.5,
    color: PAYER_COLORS[5],
  },
  {
    payer: "Allianz SF",
    code: "ALZ",
    claimsCount: 950,
    grossAmount: 245000,
    netSubmitted: 215000,
    approvedAmount: 194000,
    rejectedAmount: 21000,
    pendingAmount: 14000,
    approvalRate: 90.2,
    rejectionRate: 9.8,
    avgClaimValue: 257.8,
    color: PAYER_COLORS[6],
  },
  {
    payer: "AXA Cooperative",
    code: "AXA",
    claimsCount: 820,
    grossAmount: 210000,
    netSubmitted: 185000,
    approvedAmount: 167000,
    rejectedAmount: 18000,
    pendingAmount: 12000,
    approvalRate: 90.2,
    rejectionRate: 9.8,
    avgClaimValue: 256.0,
    color: PAYER_COLORS[7],
  },
  {
    payer: "Walaa Insurance",
    code: "WLA",
    claimsCount: 710,
    grossAmount: 185200,
    netSubmitted: 162100,
    approvedAmount: 146000,
    rejectedAmount: 16100,
    pendingAmount: 10500,
    approvalRate: 90.1,
    rejectionRate: 9.9,
    avgClaimValue: 260.8,
    color: PAYER_COLORS[8],
  },
  {
    payer: "Arabian Shield",
    code: "ARS",
    claimsCount: 572,
    grossAmount: 140000,
    netSubmitted: 123000,
    approvedAmount: 107200,
    rejectedAmount: 15800,
    pendingAmount: 7500,
    approvalRate: 87.2,
    rejectionRate: 12.8,
    avgClaimValue: 244.8,
    color: PAYER_COLORS[9],
  },
];

const FinancialSummaryTable = ({
  data = DEFAULT_FINANCIAL_SUMMARY_DATA,
  selectedPayer,
  onSelectPayer,
}) => {
  const dispatch = useDispatch();
  const reduxActivePayer = useSelector(
    (state) => state?.claimDashboard?.claimDashboardFilters?.insurance || "All",
  );

  const [internalActivePayer, setInternalActivePayer] = useState("All");
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("netSubmitted");
  const [sortAsc, setSortAsc] = useState(false);

  const currentActivePayer =
    selectedPayer !== undefined && selectedPayer !== "All"
      ? selectedPayer
      : reduxActivePayer !== "All"
        ? reduxActivePayer
        : internalActivePayer;

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(false);
    }
  };

  const handleRowClick = (payerName) => {
    const nextPayer = currentActivePayer === payerName ? "All" : payerName;
    setInternalActivePayer(nextPayer);
    if (onSelectPayer) {
      onSelectPayer(nextPayer);
    }
    dispatch(
      handleClaimDashboardFilter({
        check: "insurance",
        value: nextPayer === "All" ? null : nextPayer,
      }),
    );
  };

  const filteredData = useMemo(() => {
    return data.filter(
      (item) =>
        item.payer.toLowerCase().includes(search.toLowerCase()) ||
        (item.code && item.code.toLowerCase().includes(search.toLowerCase())),
    );
  }, [data, search]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aVal = a[sortCol];
      let bVal = b[sortCol];

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      const numA = Number(aVal) || 0;
      const numB = Number(bVal) || 0;
      return sortAsc ? numA - numB : numB - numA;
    });
  }, [filteredData, sortCol, sortAsc]);

  // Calculate totals for footer
  const totalClaims = useMemo(
    () =>
      filteredData.reduce((acc, d) => acc + (Number(d.claimsCount) || 0), 0),
    [filteredData],
  );
  const totalGross = useMemo(
    () =>
      filteredData.reduce((acc, d) => acc + (Number(d.grossAmount) || 0), 0),
    [filteredData],
  );
  const totalNet = useMemo(
    () =>
      filteredData.reduce((acc, d) => acc + (Number(d.netSubmitted) || 0), 0),
    [filteredData],
  );
  const totalApproved = useMemo(
    () =>
      filteredData.reduce((acc, d) => acc + (Number(d.approvedAmount) || 0), 0),
    [filteredData],
  );
  const totalRejected = useMemo(
    () =>
      filteredData.reduce((acc, d) => acc + (Number(d.rejectedAmount) || 0), 0),
    [filteredData],
  );
  const totalPending = useMemo(
    () =>
      filteredData.reduce((acc, d) => acc + (Number(d.pendingAmount) || 0), 0),
    [filteredData],
  );
  const avgApproval =
    totalNet > 0 ? ((totalApproved / totalNet) * 100).toFixed(1) : "0.0";
  const avgRejection =
    totalNet > 0 ? ((totalRejected / totalNet) * 100).toFixed(1) : "0.0";
  const overallAvgClaim =
    totalClaims > 0 ? (totalGross / totalClaims).toFixed(1) : "0.0";

  const renderSortIcon = (col) => {
    const isCurrent = sortCol === col;
    return (
      <ArrowDownUp
        size={11}
        className="ms-1 flex-shrink-0"
        style={{
          color: isCurrent ? "#2563EB" : "#94A3B8",
          opacity: isCurrent ? 1 : 0.7,
        }}
      />
    );
  };

  return (
    <Col xs={12} className="mt-3">
      <Card className="shadow-sm border">
        {/* Table Header Toolbar */}
        <Card.Header className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between p-3 border-bottom gap-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "8px",
                backgroundColor: "rgba(59, 130, 246, 0.12)",
                color: "#3B82F6",
                border: "1px solid rgba(59, 130, 246, 0.25)",
              }}
            >
              <TableIcon size={15} />
            </div>
            <div>
              <h6
                className="mb-0 fw-bold"
                style={{ fontSize: "15px" }}
              >
                Financial Summary by Insurance Company
              </h6>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Master financial audit log for all 10 active payers with row
                sorting
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* Search Box */}
            <div className="position-relative">
              <Search
                size={13}
                className="position-absolute text-muted"
                style={{
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
              <input
                type="text"
                placeholder="Search payer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-control form-control-sm"
                style={{
                  paddingLeft: "30px",
                  fontSize: "12px",
                  width: "180px",
                  borderColor: "#E2E8F0",
                  borderRadius: "8px",
                }}
              />
            </div>
          </div>
        </Card.Header>

        {/* Responsive Table Container with Sticky Header */}
        <div
          className="table-responsive"
          style={{ maxHeight: "460px", overflowY: "auto" }}
        >
          <Table
            className="mb-0 text-nowrap align-middle"
            style={{
              fontSize: "12px",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <thead>
              <tr style={{ fontWeight: 600 }}>
                <th
                  onClick={() => handleSort("payer")}
                  className="py-3 px-4 text-start cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center gap-1">
                    <span>Insurance Company</span>
                    {renderSortIcon("payer")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("claimsCount")}
                  className="py-3 px-3 text-end cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Claims</span>
                    {renderSortIcon("claimsCount")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("grossAmount")}
                  className="py-3 px-3 text-end cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Gross (SAR)</span>
                    {renderSortIcon("grossAmount")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("netSubmitted")}
                  className="py-3 px-3 text-end cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Net (SAR)</span>
                    {renderSortIcon("netSubmitted")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("approvedAmount")}
                  className="py-3 px-3 text-end cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Approved (SAR)</span>
                    {renderSortIcon("approvedAmount")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("rejectedAmount")}
                  className="py-3 px-3 text-end cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Rejected (SAR)</span>
                    {renderSortIcon("rejectedAmount")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("pendingAmount")}
                  className="py-3 px-3 text-end cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Pending (SAR)</span>
                    {renderSortIcon("pendingAmount")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("approvalRate")}
                  className="py-3 px-3 text-end cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Approval %</span>
                    {renderSortIcon("approvalRate")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("rejectionRate")}
                  className="py-3 px-3 text-end cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Rejection %</span>
                    {renderSortIcon("rejectionRate")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("avgClaimValue")}
                  className="py-3 px-4 text-end cursor-pointer border-bottom"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "var(--bs-card-bg, #0F172A)",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-end gap-1">
                    <span>Avg Claim</span>
                    {renderSortIcon("avgClaimValue")}
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedData.map((row, index) => {
                const isSelected =
                  currentActivePayer === row.payer ||
                  currentActivePayer === row.code;
                const dotColor =
                  row.color || PAYER_COLORS[index % PAYER_COLORS.length];

                return (
                  <tr
                    key={row.payer}
                    onClick={() => handleRowClick(row.payer)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: isSelected ? "rgba(59, 130, 246, 0.15)" : "transparent",
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    <td
                      className="py-3 px-4 font-bold border-bottom"
                      style={{
                        borderColor: "#F1F5F9",
                        fontWeight: 700,
                        borderLeft: isSelected ? "4px solid #2563EB" : "none",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            display: "inline-block",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: dotColor,
                            flexShrink: 0,
                          }}
                        ></span>
                        <span style={{ color: isSelected ? "#3B82F6" : "currentColor", fontWeight: 700 }}>
                          {row.payer}
                        </span>
                        {row.code && (
                          <span
                            style={{
                              color: "#94A3B8",
                              fontWeight: 400,
                              fontSize: "11px",
                            }}
                          >
                            ({row.code})
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className="py-3 px-3 text-end font-monospace border-bottom"
                      style={{ borderColor: "#F1F5F9", color: "currentColor" }}
                    >
                      {Number(row.claimsCount).toLocaleString("en-US")}
                    </td>
                    <td
                      className="py-3 px-3 text-end font-monospace border-bottom"
                      style={{ borderColor: "#F1F5F9", color: "currentColor" }}
                    >
                      SAR {Number(row.grossAmount).toLocaleString("en-US")}
                    </td>
                    <td
                      className="py-3 px-3 text-end font-monospace border-bottom"
                      style={{
                        borderColor: "#F1F5F9",
                        color: "#1D4ED8",
                        fontWeight: 700,
                      }}
                    >
                      SAR {Number(row.netSubmitted).toLocaleString("en-US")}
                    </td>
                    <td
                      className="py-3 px-3 text-end font-monospace border-bottom"
                      style={{
                        borderColor: "#F1F5F9",
                        color: "#059669",
                        fontWeight: 700,
                      }}
                    >
                      SAR {Number(row.approvedAmount).toLocaleString("en-US")}
                    </td>
                    <td
                      className="py-3 px-3 text-end font-monospace border-bottom"
                      style={{
                        borderColor: "#F1F5F9",
                        color: "#E11D48",
                        fontWeight: 700,
                      }}
                    >
                      SAR {Number(row.rejectedAmount).toLocaleString("en-US")}
                    </td>
                    <td
                      className="py-3 px-3 text-end font-monospace border-bottom"
                      style={{
                        borderColor: "#F1F5F9",
                        color: "#D97706",
                        fontWeight: 600,
                      }}
                    >
                      SAR {Number(row.pendingAmount).toLocaleString("en-US")}
                    </td>
                    <td
                      className="py-3 px-3 text-end border-bottom"
                      style={{ borderColor: "#F1F5F9" }}
                    >
                      <span
                        style={{
                          backgroundColor: "#D1FAE5",
                          color: "#047857",
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        {row.approvalRate}%
                      </span>
                    </td>
                    <td
                      className="py-3 px-3 text-end border-bottom"
                      style={{ borderColor: "#F1F5F9" }}
                    >
                      <span
                        style={{
                          backgroundColor: "#FFE4E6",
                          color: "#BE123C",
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        {row.rejectionRate}%
                      </span>
                    </td>
                    <td
                      className="py-3 px-4 text-end font-monospace border-bottom"
                      style={{ borderColor: "#F1F5F9", color: "currentColor" }}
                    >
                      SAR {row.avgClaimValue}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* Table Totals Footer */}
            <tfoot
              style={{
                backgroundColor: "#0F172A",
                color: "#FFFFFF",
                fontWeight: 700,
                position: "sticky",
                bottom: 0,
                zIndex: 10,
              }}
            >
              <tr>
                <td
                  className="py-3 px-4"
                  style={{
                    backgroundColor: "#0F172A",
                    borderTop: "1px solid #334155",
                  }}
                >
                  TOTALS / OVERALL
                </td>
                <td
                  className="py-3 px-3 text-end font-monospace"
                  style={{
                    backgroundColor: "#0F172A",
                    borderTop: "1px solid #334155",
                  }}
                >
                  {totalClaims.toLocaleString("en-US")}
                </td>
                <td
                  className="py-3 px-3 text-end font-monospace"
                  style={{
                    backgroundColor: "#0F172A",
                    borderTop: "1px solid #334155",
                  }}
                >
                  SAR {totalGross.toLocaleString("en-US")}
                </td>
                <td
                  className="py-3 px-3 text-end font-monospace"
                  style={{
                    backgroundColor: "#0F172A",
                    color: "#93C5FD",
                    borderTop: "1px solid #334155",
                  }}
                >
                  SAR {totalNet.toLocaleString("en-US")}
                </td>
                <td
                  className="py-3 px-3 text-end font-monospace"
                  style={{
                    backgroundColor: "#0F172A",
                    color: "#34D399",
                    borderTop: "1px solid #334155",
                  }}
                >
                  SAR {totalApproved.toLocaleString("en-US")}
                </td>
                <td
                  className="py-3 px-3 text-end font-monospace"
                  style={{
                    backgroundColor: "#0F172A",
                    color: "#FB7185",
                    borderTop: "1px solid #334155",
                  }}
                >
                  SAR {totalRejected.toLocaleString("en-US")}
                </td>
                <td
                  className="py-3 px-3 text-end font-monospace"
                  style={{
                    backgroundColor: "#0F172A",
                    color: "#FDE047",
                    borderTop: "1px solid #334155",
                  }}
                >
                  SAR {totalPending.toLocaleString("en-US")}
                </td>
                <td
                  className="py-3 px-3 text-end"
                  style={{
                    backgroundColor: "#0F172A",
                    color: "#34D399",
                    borderTop: "1px solid #334155",
                  }}
                >
                  {avgApproval}%
                </td>
                <td
                  className="py-3 px-3 text-end"
                  style={{
                    backgroundColor: "#0F172A",
                    color: "#FB7185",
                    borderTop: "1px solid #334155",
                  }}
                >
                  {avgRejection}%
                </td>
                <td
                  className="py-3 px-4 text-end font-monospace"
                  style={{
                    backgroundColor: "#0F172A",
                    borderTop: "1px solid #334155",
                  }}
                >
                  SAR {overallAvgClaim}
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </Card>
    </Col>
  );
};

export default FinancialSummaryTable;
